import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2, Home, Dumbbell, Utensils, User,
  Plus, ClipboardList, Heart, Bot, LineChart, BookOpen,
} from 'lucide-react';
import TitleBar from '../components/TitleBar';
import Footer from '../components/Footer';
import CompleteWorkoutSheet from '../components/CompleteWorkoutSheet';
import ReferralPromptCard from '../components/ReferralPromptCard';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';

import PlanForm from '../components/plan/PlanForm';
import { getOnboardingData } from '../components/Onboarding';
import TargetsCard from '../components/plan/TargetsCard';
import DailyCallCard from '../components/plan/DailyCallCard';
import RecoveryCard from '../components/plan/RecoveryCard';
import MealPlanCard from '../components/plan/MealPlanCard';
import WeeklyMealPlanner from '../components/nutrition/WeeklyMealPlanner';
import DailyMacroTracker from '../components/nutrition/DailyMacroTracker';
import FoodSnapCard from '../components/plan/FoodSnapCard';
import Fuel from '../components/plan/Fuel';
import OverloadCard from '../components/plan/OverloadCard';
import CoachChat from '../components/plan/CoachChat';
import ProgressBoard from '../components/plan/Progress';
import ExerciseLibrary from '../components/plan/ExerciseLibrary';
import ProgressionStrip from '../components/plan/ProgressionStrip';
import Routines from '../components/plan/Routines';
import Mastery from '../components/plan/Mastery';
import MentalWellbeing from '../components/plan/MentalWellbeing';

// 4 top-level tabs (per /app/design_guidelines.json)
const TABS = [
  { id: 'home',      label: 'Home',      icon: Home,     testid: 'nav-home-tab' },
  { id: 'training',  label: 'Training',  icon: Dumbbell, testid: 'nav-training-tab' },
  { id: 'nutrition', label: 'Nutrition', icon: Utensils, testid: 'nav-nutrition-tab' },
  { id: 'profile',   label: 'Profile',   icon: User,     testid: 'nav-profile-tab' },
];

// Sub-tabs within Training (horizontal pills)
const TRAINING_SUBS = [
  { id: 'routines',  label: 'Routines',  icon: ClipboardList },
  { id: 'mastery',   label: 'Mastery',   icon: BookOpen },
  { id: 'library',   label: 'Library',   icon: BookOpen },
  { id: 'recovery',  label: 'Physio',    icon: Heart },
  { id: 'wellbeing', label: 'Wellbeing', icon: Heart },
];

// Sub-tabs within Profile
const PROFILE_SUBS = [
  { id: 'progress', label: 'Progress', icon: LineChart },
  { id: 'plan',     label: 'My Plan',  icon: ClipboardList },
  { id: 'coach',    label: 'AI Coach', icon: Bot },
];

// Sub-tabs within Nutrition
const NUTRITION_SUBS = [
  { id: 'plan',   label: 'Plan',   icon: Utensils },
  { id: 'fuel',   label: 'Fuel',   icon: Heart },
];

const BottomNav = ({ active, onChange, onLogLift }) => (
  <nav
    className="lg:hidden fixed bottom-0 left-0 right-0 z-[10000] bg-[#0b0b0c]/95 backdrop-blur border-t border-zinc-800"
    style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    data-testid="bottom-nav"
  >
    <div className="relative grid grid-cols-5 items-end">
      {TABS.slice(0, 2).map((t) => <NavBtn key={t.id} t={t} active={active} onChange={onChange} />)}
      {/* FAB occupies the centre slot */}
      <div className="relative flex justify-center">
        <button
          onClick={onLogLift}
          data-testid="fab-log-lift"
          aria-label="Log a lift"
          className="absolute -top-6 h-14 w-14 rounded-full bg-[#BEFF38] text-black grid place-items-center shadow-[0_8px_24px_-4px_rgba(190,255,56,0.55)] active:scale-95 transition-transform border-4 border-[#0b0b0c]"
        >
          <Dumbbell className="h-6 w-6" strokeWidth={2.75} />
        </button>
        <span className="pb-2 text-[10px] font-bold uppercase tracking-wider text-[#BEFF38]">Log Lift</span>
      </div>
      {TABS.slice(2).map((t) => <NavBtn key={t.id} t={t} active={active} onChange={onChange} />)}
    </div>
  </nav>
);

const NavBtn = ({ t, active, onChange }) => {
  const Icon = t.icon;
  const isActive = active === t.id;
  return (
    <button
      onClick={() => onChange(t.id)}
      data-testid={t.testid}
      className={`w-full flex flex-col items-center gap-0.5 py-2.5 transition-colors ${
        isActive ? 'text-[#BEFF38]' : 'text-zinc-500 hover:text-zinc-300'
      }`}
    >
      <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
      <span className="text-[10px] font-bold uppercase tracking-wider leading-tight">{t.label}</span>
    </button>
  );
};

// Desktop left side-nav (4 main tabs + Log Lift CTA + tactical snapshot)
const SideNav = ({ active, onChange, onLogLift }) => (
  <nav className="hidden lg:block lg:sticky lg:top-24 self-start space-y-4">
    <ul className="flex flex-col gap-1">
      {TABS.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.id;
        return (
          <li key={t.id}>
            <button
              onClick={() => onChange(t.id)}
              data-testid={`${t.testid}-desktop`}
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-colors ${
                isActive
                  ? 'bg-[#BEFF38]/10 text-[#BEFF38] border-l-2 border-[#BEFF38] pl-[10px]'
                  : 'text-zinc-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={isActive ? 2.5 : 2} /> {t.label}
            </button>
          </li>
        );
      })}
    </ul>
    <button
      onClick={onLogLift}
      data-testid="log-lift-desktop"
      className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#BEFF38] text-black font-black uppercase tracking-widest text-xs hover:bg-[#a8e62e] shadow-[0_4px_20px_-4px_rgba(190,255,56,0.4)] transition-colors"
    >
      <Plus className="w-4 h-4" strokeWidth={3} /> Log Lift
    </button>
  </nav>
);

const SubTabs = ({ subs, active, onChange, idPrefix }) => (
  <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
    {subs.map((s) => {
      const Icon = s.icon;
      const isActive = active === s.id;
      return (
        <button
          key={s.id}
          onClick={() => onChange(s.id)}
          data-testid={`${idPrefix}-${s.id}`}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-black uppercase tracking-widest transition-colors ${
            isActive
              ? 'bg-[#BEFF38] text-black shadow-[0_2px_10px_-2px_rgba(190,255,56,0.4)]'
              : 'bg-transparent text-zinc-400 border border-zinc-800 hover:border-zinc-600 hover:text-zinc-200'
          }`}
        >
          <Icon className="w-3.5 h-3.5" /> {s.label}
        </button>
      );
    })}
  </div>
);

const MyPlan = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const validIds = useMemo(() => new Set(TABS.map((t) => t.id)), []);
  const initialTab = (() => {
    const t = searchParams.get('tab');
    return t && validIds.has(t) ? t : 'home';
  })();
  const [active, setActive] = useState(initialTab);
  const [trainingSub, setTrainingSub] = useState(searchParams.get('sub') || 'routines');
  const [profileSub, setProfileSub] = useState(searchParams.get('sub') || 'progress');
  const [nutritionSub, setNutritionSub] = useState(searchParams.get('sub') || 'plan');
  const [logOpen, setLogOpen] = useState(false);
  const [logPrefill, setLogPrefill] = useState([]);

  // Listen for "open-log-sheet" events dispatched by ExerciseLibrary cards
  useEffect(() => {
    const handler = (e) => {
      const ex = e?.detail?.exercise;
      if (ex) {
        setLogPrefill([{
          id: ex.id,
          name: ex.name,
          sets: 3,
          reps: 8,
          rest: 90,
        }]);
        setLogOpen(true);
      }
    };
    window.addEventListener('open-log-sheet', handler);
    return () => window.removeEventListener('open-log-sheet', handler);
  }, []);

  const setTab = (id) => {
    setActive(id);
    // Reset sub-tab to a sensible default for the new top-tab
    if (id === 'training') setTrainingSub('routines');
    if (id === 'profile') setProfileSub('progress');
  };

  // Sync URL when tab changes (so deep-links + back button work)
  useEffect(() => {
    const current = searchParams.get('tab');
    if (active !== current) {
      const next = new URLSearchParams(searchParams);
      if (active === 'home') next.delete('tab'); else next.set('tab', active);
      next.delete('sub');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [active]);

  const [plan, setPlan] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [call, setCall] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genRecovery, setGenRecovery] = useState(false);
  const [loading, setLoading] = useState(true);
  const [targetsKey, setTargetsKey] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        let [p, cs, lc, rp] = await Promise.all([
          api.getPlan(), api.listCheckins(), api.latestDailyCall(), api.latestRecoveryPlan(),
        ]);

        // Auto-create a starter plan from onboarding data so Generate is immediately
        // usable. The user can tweak it later in Profile → My Plan.
        if (!p) {
          const onb = getOnboardingData();
          if (onb?.goal) {
            const goalMap = { lose_fat: 'lose_fat', build_muscle: 'build_muscle', get_stronger: 'build_muscle', mobility: 'general_health', endurance: 'improve_endurance', maintain: 'maintain' };
            const planTypeMap = { lose_fat: 'cut', build_muscle: 'bulk', get_stronger: 'bulk', mobility: 'maintain', endurance: 'maintain', maintain: 'maintain' };
            try {
              p = await api.upsertPlan({
                plan_type: planTypeMap[onb.goal] || 'maintain',
                transform_stage: 0,
                goal: goalMap[onb.goal] || 'general_health',
                sex: 'male', age: 30, height_cm: 178, weight_kg: 80,
                activity: 'moderate',
                weekly_rate_kg: 0.5,
                equipment: onb.equipment && onb.equipment.length ? onb.equipment : ['Bodyweight'],
                injuries: onb.injuries || [],
              });
            } catch { /* ignore — user can save manually */ }
          }
        }

        setPlan(p); setCheckins(cs || []); setCall(lc); setRecovery(rp);
      } catch (err) {
        toast({ title: 'Could not load data', description: err.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const onPlanSaved = (saved) => {
    setPlan(saved);
    setTargetsKey((k) => k + 1);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const c = await api.generateDailyCall();
      setCall(c);
      toast({ title: 'Daily call ready', description: c.focus });
    } catch (err) {
      toast({ title: 'Generation failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setGenerating(false); }
  };

  const generateRecovery = async () => {
    setGenRecovery(true);
    try {
      const r = await api.generateRecoveryPlan();
      setRecovery(r);
      toast({ title: 'Recovery plan ready' });
    } catch (err) {
      toast({ title: 'Generation failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setGenRecovery(false); }
  };

  // Home tab: Daily Call is the primary answer to "what should I do now?".
  // Referral/progression become secondary ribbons below.
  const renderHome = () => (
    <div className="space-y-5">
      <ProgressionStrip refreshKey={targetsKey} />
      <DailyCallCard call={call} plan={plan} onGenerate={generate} generating={generating} canGenerate={!!plan} />
      <TargetsCard refreshKey={targetsKey} />
      <ReferralPromptCard variant="home" testid="home-referral-card" />
      <div className="grid sm:grid-cols-2 gap-4">
        <button
          onClick={() => { setActive('profile'); setProfileSub('coach'); }}
          data-testid="home-coach-shortcut"
          className="text-left rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 hover:border-[#BEFF38]/40 transition-colors"
        >
          <Bot className="w-5 h-5 text-[#BEFF38]" />
          <p className="mt-2 text-white font-black uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>AI Coach</p>
          <p className="text-xs text-zinc-400 mt-1">Ask anything — form, programming, nutrition.</p>
        </button>
        <button
          onClick={() => { setActive('training'); setTrainingSub('recovery'); }}
          data-testid="home-recovery-shortcut"
          className="text-left rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 hover:border-[#BEFF38]/40 transition-colors"
        >
          <Heart className="w-5 h-5 text-[#BEFF38]" />
          <p className="mt-2 text-white font-black uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Recovery</p>
          <p className="text-xs text-zinc-400 mt-1">Daily mobility & deload guidance.</p>
        </button>
      </div>
    </div>
  );

  const renderTraining = () => (
    <div className="space-y-5">
      <SubTabs subs={TRAINING_SUBS} active={trainingSub} onChange={setTrainingSub} idPrefix="training-sub" />
      {trainingSub === 'routines' && (
        <div className="space-y-5">
          <Routines />
          <OverloadCard />
        </div>
      )}
      {trainingSub === 'mastery' && <Mastery refreshHome={() => setTargetsKey((k) => k + 1)} />}
      {trainingSub === 'library' && <ExerciseLibrary />}
      {trainingSub === 'recovery' && (
        <RecoveryCard rp={recovery} onGenerate={generateRecovery} generating={genRecovery} canGenerate={!!plan} />
      )}
      {trainingSub === 'wellbeing' && <MentalWellbeing />}
    </div>
  );

  const renderNutrition = () => (
    <div className="space-y-5">
      <SubTabs subs={NUTRITION_SUBS} active={nutritionSub} onChange={setNutritionSub} idPrefix="nutrition-sub" />
      {nutritionSub === 'plan' && (
        <>
          <TargetsCard refreshKey={targetsKey} />
          <DailyMacroTracker />
          <WeeklyMealPlanner />
          <MealPlanCard canGenerate={!!plan} />
          <FoodSnapCard />
        </>
      )}
      {nutritionSub === 'fuel' && <Fuel />}
    </div>
  );

  const renderProfile = () => (
    <div className="space-y-5">
      <ReferralPromptCard variant="profile" testid="profile-referral-btn" />
      <SubTabs subs={PROFILE_SUBS} active={profileSub} onChange={setProfileSub} idPrefix="profile-sub" />
      {profileSub === 'progress' && <ProgressBoard initialCheckins={checkins} />}
      {profileSub === 'plan' && (() => {
        const onb = getOnboardingData();
        const goalMap = { lose_fat: 'lose_fat', build_muscle: 'build_muscle', get_stronger: 'build_muscle', mobility: 'general_health', endurance: 'improve_endurance', maintain: 'maintain' };
        const planTypeMap = { lose_fat: 'cut', build_muscle: 'bulk', get_stronger: 'bulk', mobility: 'maintain', endurance: 'maintain', maintain: 'maintain' };
        const prefill = plan ? plan : (onb?.goal ? {
          goal: goalMap[onb.goal] || 'general_health',
          plan_type: planTypeMap[onb.goal] || 'maintain',
          equipment: onb.equipment && onb.equipment.length ? onb.equipment : ['Bodyweight'],
          injuries: onb.injuries || [],
        } : null);
        return <PlanForm initial={prefill} onSaved={onPlanSaved} />;
      })()}
      {profileSub === 'coach' && <CoachChat />}
    </div>
  );

  const Panel = (() => {
    if (active === 'home') return renderHome();
    if (active === 'training') return renderTraining();
    if (active === 'nutrition') return renderNutrition();
    if (active === 'profile') return renderProfile();
    return null;
  })();

  const activeMeta = TABS.find((t) => t.id === active);
  const eyebrow = active === 'home' ? "Today's briefing" : `Section · ${activeMeta?.label}`;
  const heading = active === 'home' ? "Today" : activeMeta?.label;
  const subhead = ({
    home: 'One adaptive engine — training, nutrition, recovery.',
    training: 'Routines, library, physio and recovery sessions.',
    nutrition: 'Targets, weekly meal plan and food snap.',
    profile: 'Progress, plan settings and your AI coach.',
  })[active] || '';

  return (
    <div className="min-h-screen bg-[#0b0b0c]">
      <TitleBar
        title={activeMeta?.label || 'Today'}
        back="/"
      />
      <main className="max-w-[1280px] mx-auto px-4 lg:px-8 py-5 lg:py-10 pb-32 lg:pb-16">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] font-black text-[#BEFF38]">
              {eyebrow}
            </p>
            <h1
              className="mt-1 text-white font-black tracking-tight text-3xl lg:text-4xl uppercase leading-none"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {heading}
            </h1>
            {subhead && (
              <p className="mt-1.5 text-zinc-500 max-w-2xl text-xs sm:text-sm">{subhead}</p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="mt-6 grid lg:grid-cols-[220px_1fr] gap-6 lg:gap-8">
            <SideNav active={active} onChange={setTab} onLogLift={() => setLogOpen(true)} />
            <div className="min-w-0">{Panel}</div>
          </div>
        )}
      </main>
      <BottomNav active={active} onChange={setTab} onLogLift={() => setLogOpen(true)} />
      <CompleteWorkoutSheet
        open={logOpen}
        onClose={() => { setLogOpen(false); setLogPrefill([]); }}
        source={logPrefill.length ? "library_quick_log" : "quick_log"}
        title={logPrefill.length ? `Log ${logPrefill[0].name}` : "Quick log"}
        exercises={logPrefill}
        onCompleted={() => setTargetsKey((k) => k + 1)}
      />
      <Footer />
    </div>
  );
};

export default MyPlan;
